<template>
  <div class="pub-finder">
    <div class="map-container">
      <div class="search-bar">
        <input
          type="text"
          placeholder="Search Location"
          v-model="searchQuery"
          @keydown.enter="searchLocation"
        />
        <button class="search" @click="searchLocation"></button>
      </div>

      <ul class="map-toggle-options">
        <li :class="{ active: selectedCategory === 'board' }">
          <a href="#" class="board" @click.prevent="selectedCategory = 'board'">
            Play</a>
        </li>
        <li :class="{ active: selectedCategory === 'tv' }">
          <a href="#" class="tv" @click.prevent="selectedCategory = 'tv'">Watch</a>
        </li>
        <li :class="{ active: selectedCategory === 'both' }">
          <a href="#" class="both" @click.prevent="selectedCategory = 'both'">Drink</a>
        </li>
      </ul>

      <div class="map">
        <GoogleMap
          api-key="AIzaSyCoO_PbhMZwVry_IQmoHHvGpm6tBgOIB90"
          style="width: 100%; height: 500px"
          :center="center"
          :zoom="15"
        >
          <Marker
            v-for="(location, i) in filteredLocations"
            :key="i"
            :options="{
              position: { lat: location.lat, lng: location.lng },
              icon: customMarkerIcon,
            }"
            @click="selectPub(location)"
          />
        </GoogleMap>
      </div>

      <div class="locations-list">
        <div class="location" v-for="(location, i) in radiusLocations" :key="i">
          <div class="marker"></div>
          <div>
            <h4>{{ location.name }}</h4>
            <p>{{ location.address }}</p>
            <p>{{ location.city }}</p>
            <p>{{ location.postcode }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, computed } from 'vue';
import { GoogleMap, Marker, MarkerCluster, InfoWindow } from 'vue3-google-map';
import axios from 'axios';
import customMarkerIcon from '@/assets/map-marker.svg';

const center = ref({ lat: 51.50739, lng: -0.12781 });
const locations = ref([]);
const searchQuery = ref('');
const selectedCategory = ref('both');
const radiusInMiles = 0.3;
const selectedPub = ref(null);

const fetchLocations = async () => {
  try {
    const response = await axios.get('/pubDataExport.json');
    locations.value = response.data
      .filter(
        (location) =>
          location.geoCoordinate &&
          location.geoCoordinate.latitude &&
          location.geoCoordinate.longitude
      )
      .map((location) => ({
        lat: location.geoCoordinate.latitude,
        lng: location.geoCoordinate.longitude,
        category: location.type,
        name: location.locationName,
        address: location.locationAddress.addressLine1,
        city: location.locationAddress.city,
        postcode: location.locationAddress.postCode,
      }));
  } catch (error) {
    alert('Failed to load locations. Please try again.');
  }
};

const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const toRadians = (degrees) => degrees * (Math.PI / 180);
  const earthRadiusMiles = 3958.8; // Radius of Earth in miles

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMiles * c;
};

const selectPub = (location) => {
  selectedPub.value = location;
};

const filteredLocations = computed(() => {
  if (selectedCategory.value === 'both') {
    return locations.value;
  }
  return locations.value.filter((location) => location.category === selectedCategory.value);
});

const radiusLocations = computed(() => {
  const filtered = locations.value.filter((location) => {
    const distance = calculateDistance(
      center.value.lat,
      center.value.lng,
      location.lat,
      location.lng
    );
    const isWithinRadius = distance <= radiusInMiles;
    const matchesCategory =
      selectedCategory.value === 'both' || location.category === selectedCategory.value;
    return isWithinRadius && matchesCategory;
  });

  // If a pub is selected, move it to the top of the list
  if (selectedPub.value) {
    return [selectedPub.value, ...filtered.filter((location) => location !== selectedPub.value)];
  }

  return filtered;
});

const searchLocation = async () => {
  if (!searchQuery.value) {
    alert('Please enter a location to search.');
    return;
  }

  try {
    const response = await axios.get('https://maps.googleapis.com/maps/api/geocode/json', {
      params: {
        address: searchQuery.value,
        key: 'AIzaSyCoO_PbhMZwVry_IQmoHHvGpm6tBgOIB90',
      },
    });

    if (response.data.results.length > 0) {
      const location = response.data.results[0].geometry.location;
      center.value = { lat: location.lat, lng: location.lng };
    } else {
      alert('Location not found.');
    }
  } catch (error) {
    console.error('Error fetching location:', error);
    alert('Failed to fetch location. Please try again.');
  }
};

// Function to get the user's current location
const getUserLocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        center.value = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
      },
      (error) => {
        console.error('Error getting user location:', error);
        alert('Unable to retrieve your location. Using default location.');
      }
    );
  } else {
    alert('Geolocation is not supported by your browser.');
  }
};

onMounted(() => {
  getUserLocation();
  fetchLocations();
});
</script>
